import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { money } from "../../lib/format";
import PageHeader from "../../components/PageHeader";

const FEATURE_CODES = [
  "POS",
  "ORDERS",
  "REPORTS",
  "INVENTORY",
  "KITCHEN",
  "MULTI_OUTLET",
  "DISCOUNTS",
  "ADVANCED_REPORTS",
  "AUDIT_LOG",
  "OFFLINE_POS",
  "RECEIPT_PRINTING",
  "BARCODE",
];

export default function PlatformPlansPage() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () =>
    api
      .get("/platform/plans")
      .then((r) => setPlans(r.data))
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
  }, []);

  const startEditing = (plan) => {
    const copy = JSON.parse(JSON.stringify(plan));

    setEditing({
      ...copy,
      public: copy.public !== false,
      launch_price: copy.launch_price ?? "",
      pricing_options: {
        MONTHLY: {
          price: copy.pricing_options?.MONTHLY?.price ?? copy.price ?? "",
          launch_price: copy.pricing_options?.MONTHLY?.launch_price ?? copy.launch_price ?? "",
        },
        YEARLY: {
          price: copy.pricing_options?.YEARLY?.price ?? "",
          launch_price: copy.pricing_options?.YEARLY?.launch_price ?? "",
        },
      },
      promotion: {
        enabled: copy.promotion?.enabled === true,
        start_date: copy.promotion?.start_date ?? "",
        end_date: copy.promotion?.end_date ?? "",
      },
      lifetime_price: copy.lifetime_price ?? "",
    });
  };

  const save = async () => {
    if (!editing) return;

    const regularPrice = parseInt(editing.price, 10) || 0;

    let launchPrice = null;

    if (editing.launch_price !== "" && editing.launch_price != null) {
      launchPrice = parseInt(editing.launch_price, 10);

      if (Number.isNaN(launchPrice) || launchPrice < 0) {
        toast.error("Launch Price harus berupa angka 0 atau lebih.");
        return;
      }
    }

    if (regularPrice < 0) {
      toast.error("Regular Price tidak boleh negatif.");
      return;
    }

    if (
      launchPrice !== null &&
      launchPrice > regularPrice
    ) {
      toast.error("Launch Price tidak boleh lebih besar dari Regular Price.");
      return;
    }

    for (const interval of ["MONTHLY", "YEARLY"]) {
      const prices = editing.pricing_options?.[interval] || {};
      const regular = prices.price;
      const promo = prices.launch_price;

      if (
        regular === "" || regular == null ||
        !Number.isSafeInteger(Number(regular)) ||
        Number(regular) <= 0
      ) {
        toast.error(`Harga reguler ${interval} harus berupa bilangan bulat positif.`);
        return;
      }

      if (promo !== "" && promo != null) {
        if (
          !Number.isSafeInteger(Number(promo)) ||
          Number(promo) < 0 ||
          Number(promo) > Number(regular)
        ) {
          toast.error(`Harga promo ${interval} tidak valid atau melebihi harga reguler.`);
          return;
        }
      }
    }

    const lifetime = editing.lifetime_price;
    if (
      lifetime !== "" && lifetime != null &&
      (!Number.isSafeInteger(Number(lifetime)) || Number(lifetime) < 0)
    ) {
      toast.error("Harga lifetime harus berupa bilangan bulat 0 atau lebih.");
      return;
    }

    const promotion = editing.promotion || {};
    if (promotion.enabled) {
      if (!promotion.start_date || !promotion.end_date) {
        toast.error("Promo aktif wajib memiliki tanggal mulai dan berakhir.");
        return;
      }
      if (promotion.start_date > promotion.end_date) {
        toast.error("Tanggal mulai promo tidak boleh melewati tanggal berakhir.");
        return;
      }
    }

    const outletLimit = editing.features?.OUTLET_LIMIT?.limit;
    const userLimit = editing.features?.USER_LIMIT?.limit;
    const dailyReceiptLimit =
      editing.features?.DAILY_RECEIPT_LIMIT?.limit;

    if (
      outletLimit != null &&
      (!Number.isInteger(outletLimit) || outletLimit < 1)
    ) {
      toast.error("Outlet limit harus kosong atau minimal 1.");
      return;
    }

    if (
      userLimit != null &&
      (!Number.isInteger(userLimit) || userLimit < 1)
    ) {
      toast.error("User limit harus kosong atau minimal 1.");
      return;
    }

    if (
      dailyReceiptLimit != null &&
      (!Number.isInteger(dailyReceiptLimit) || dailyReceiptLimit < 1)
    ) {
      toast.error("Daily receipt limit harus kosong atau minimal 1.");
      return;
    }

    try {
      await api.patch(`/platform/plans/${editing.code}`, {
        price: regularPrice,
        launch_price: launchPrice,
        pricing_options: {
          MONTHLY: {
            price: Number(editing.pricing_options?.MONTHLY?.price ?? regularPrice),
            launch_price: editing.pricing_options?.MONTHLY?.launch_price === "" ||
              editing.pricing_options?.MONTHLY?.launch_price == null
              ? null
              : Number(editing.pricing_options.MONTHLY.launch_price),
          },
          YEARLY: {
            price: Number(editing.pricing_options?.YEARLY?.price ?? 0),
            launch_price: editing.pricing_options?.YEARLY?.launch_price === "" ||
              editing.pricing_options?.YEARLY?.launch_price == null
              ? null
              : Number(editing.pricing_options.YEARLY.launch_price),
          },
        },
        promotion: editing.promotion,
        lifetime_price: editing.lifetime_price === "" ||
          editing.lifetime_price == null
          ? null
          : Number(editing.lifetime_price),
        active: editing.active,
        public: editing.public,
        features: editing.features,
      });

      toast.success(`Plan ${editing.code} updated`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setFeature = (code, enabled) =>
    setEditing((s) => ({
      ...s,
      features: {
        ...s.features,
        [code]: {
          ...s.features?.[code],
          enabled,
        },
      },
    }));

  const setLimit = (code, val) =>
    setEditing((s) => ({
      ...s,
      features: {
        ...s.features,
        [code]: {
          enabled: true,
          limit:
            val === ""
              ? null
              : parseInt(val, 10),
        },
      },
    }));

  return (
    <div data-testid="platform-plans-page">
      <PageHeader
        title="Subscription Plans"
        subtitle="Feature entitlements & resource limits per plan"
        testid="platform-plans-header"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {plans.map((p) => (
          <button
            key={p.code}
            onClick={() => startEditing(p)}
            data-testid={`plan-card-${p.code.toLowerCase()}`}
            className="text-left bg-card border border-border rounded-2xl p-5 hover:bg-secondary/40 transition"
          >
            <div className="flex justify-between items-start">
              <h3 className="font-heading text-xl font-extrabold text-primary">
                {p.name}
              </h3>

              <span
                className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                  p.active
                    ? "bg-emerald-500/15 text-emerald-400"
                    : "bg-red-500/15 text-red-400"
                }`}
              >
                {p.active ? "ACTIVE" : "INACTIVE"}
              </span>
            </div>

            <p className="font-mono font-bold mt-1">
              {money(p.price)}
              <span className="text-xs text-muted-foreground font-sans">
                /{p.billing_interval === "YEARLY" ? "yr" : "mo"}
              </span>
            </p>

            {p.launch_price != null && (
              <p className="text-xs font-semibold text-primary mt-1">
                Launch: {money(p.launch_price)}
                <span className="text-muted-foreground font-normal">
                  /{p.billing_interval === "YEARLY" ? "yr" : "mo"}
                </span>
              </p>
            )}

            <p className="text-xs text-muted-foreground mt-2">
              {p.features?.OUTLET_LIMIT?.limit == null
                ? "∞"
                : p.features?.OUTLET_LIMIT?.limit}{" "}
              outlets ·{" "}
              {p.features?.USER_LIMIT?.limit == null
                ? "∞"
                : p.features?.USER_LIMIT?.limit}{" "}
              users
            </p>

            <p className="text-xs text-muted-foreground mt-1">
              {p.features?.DAILY_RECEIPT_LIMIT?.limit == null
                ? "∞"
                : p.features?.DAILY_RECEIPT_LIMIT?.limit}{" "}
              receipts/day
            </p>

            <p className="text-xs text-muted-foreground mt-1">
              {p.public === false ? "Internal plan" : "Public plan"}
            </p>

            <div className="flex flex-wrap gap-1 mt-3">
              {FEATURE_CODES.filter(
                (c) => p.features?.[c]?.enabled
              ).map((c) => (
                <span
                  key={c}
                  className="px-1.5 py-0.5 rounded bg-secondary text-[9px] font-bold text-muted-foreground"
                >
                  {c}
                </span>
              ))}
            </div>

            <span
              className="inline-flex items-center gap-1 mt-3 text-[11px] font-bold text-primary"
              data-testid={`plan-edit-hint-${p.code.toLowerCase()}`}
            >
              Edit plan →
            </span>
          </button>
        ))}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-card border border-border w-full max-w-lg rounded-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="plan-edit-modal"
          >
            <h3 className="font-heading text-lg font-bold">
              Edit Plan · {editing.code}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">
                  Regular Price
                </p>

                <input
                  data-testid="plan-edit-price"
                  type="number"
                  min="0"
                  value={editing.price ?? ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      price: e.target.value,
                    })
                  }
                  className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm font-mono focus:border-primary focus:outline-none"
                />
              </div>

              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">
                  Launch Price
                </p>

                <input
                  data-testid="plan-edit-launch-price"
                  type="number"
                  min="0"
                  value={editing.launch_price ?? ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      launch_price: e.target.value,
                    })
                  }
                  placeholder="Kosong = null"
                  className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm font-mono focus:border-primary focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <label className="flex items-center gap-2 text-sm bg-secondary/40 rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={!!editing.active}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      active: e.target.checked,
                    })
                  }
                  data-testid="plan-edit-active"
                />
                Active
              </label>

              <label className="flex items-center gap-2 text-sm bg-secondary/40 rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={editing.public !== false}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      public: e.target.checked,
                    })
                  }
                  data-testid="plan-edit-public"
                />
                Public
              </label>
            </div>

            <div className="border border-border rounded-xl p-4 space-y-4">
              <h4 className="font-semibold text-sm">Harga Bulanan & Tahunan (Rupiah)</h4>
              {["MONTHLY", "YEARLY"].map((interval) => (
                <div key={interval} className="space-y-2">
                  <p className="text-sm font-semibold">
                    {interval === "MONTHLY" ? "Bulanan" : "Tahunan"}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {["price", "launch_price"].map((field) => (
                      <label key={field} className="text-xs text-muted-foreground">
                        {field === "price" ? "Harga reguler" : "Harga promo"}
                        <input
                          type="number"
                          min="0"
                          value={editing.pricing_options?.[interval]?.[field] ?? ""}
                          onChange={(e) => setEditing({
                            ...editing,
                            pricing_options: {
                              ...editing.pricing_options,
                              [interval]: {
                                ...editing.pricing_options?.[interval],
                                [field]: e.target.value,
                              },
                            },
                          })}
                          placeholder={field === "launch_price" ? "Kosong = tanpa promo" : "Harga Rupiah"}
                          className="w-full h-10 mt-1 px-3 rounded-lg bg-secondary border border-border text-sm text-foreground"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="border border-border rounded-xl p-4 space-y-3">
              <h4 className="font-semibold text-sm">Periode Promo</h4>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.promotion?.enabled === true}
                  onChange={(e) => setEditing({
                    ...editing,
                    promotion: {...editing.promotion, enabled: e.target.checked},
                  })}
                />
                Aktifkan promo
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  ["start_date", "Tanggal mulai"],
                  ["end_date", "Tanggal berakhir"],
                ].map(([field, label]) => (
                  <label key={field} className="text-xs text-muted-foreground">
                    {label}
                    <input
                      type="date"
                      value={editing.promotion?.[field] ?? ""}
                      onChange={(e) => setEditing({
                        ...editing,
                        promotion: {...editing.promotion, [field]: e.target.value},
                      })}
                      className="w-full h-10 mt-1 px-3 rounded-lg bg-secondary border border-border text-sm text-foreground"
                    />
                  </label>
                ))}
              </div>
            </div>

            <label className="block text-xs font-semibold text-muted-foreground">
              Harga Lifetime (Rupiah, konfigurasi saja)
              <input
                type="number"
                min="0"
                value={editing.lifetime_price ?? ""}
                onChange={(e) => setEditing({
                  ...editing,
                  lifetime_price: e.target.value,
                })}
                placeholder="Kosong = belum ditentukan"
                className="w-full h-10 mt-1 px-3 rounded-lg bg-secondary border border-border text-sm text-foreground"
              />
              <span className="block mt-1 font-normal">
                Checkout lifetime belum diaktifkan.
              </span>
            </label>

            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">
                Features
              </p>

              <div className="grid grid-cols-2 gap-1.5">
                {FEATURE_CODES.map((c) => (
                  <label
                    key={c}
                    className="flex items-center gap-2 text-xs bg-secondary/40 rounded-lg px-2.5 py-2"
                  >
                    <input
                      type="checkbox"
                      checked={
                        !!editing.features?.[c]?.enabled
                      }
                      onChange={(e) =>
                        setFeature(c, e.target.checked)
                      }
                      data-testid={`plan-feature-${c.toLowerCase()}`}
                    />{" "}
                    {c}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">
                  Outlet limit
                </p>

                <input
                  data-testid="plan-edit-outlet-limit"
                  type="number"
                  min="1"
                  value={
                    editing.features?.OUTLET_LIMIT?.limit ?? ""
                  }
                  onChange={(e) =>
                    setLimit(
                      "OUTLET_LIMIT",
                      e.target.value
                    )
                  }
                  placeholder="Unlimited"
                  className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm font-mono focus:border-primary focus:outline-none"
                />

                <p className="text-[10px] text-muted-foreground mt-1">
                  Kosong = unlimited
                </p>
              </div>

              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">
                  User limit
                </p>

                <input
                  data-testid="plan-edit-user-limit"
                  type="number"
                  min="1"
                  value={
                    editing.features?.USER_LIMIT?.limit ?? ""
                  }
                  onChange={(e) =>
                    setLimit(
                      "USER_LIMIT",
                      e.target.value
                    )
                  }
                  placeholder="Unlimited"
                  className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm font-mono focus:border-primary focus:outline-none"
                />

                <p className="text-[10px] text-muted-foreground mt-1">
                  Kosong = unlimited
                </p>
              </div>

              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">
                  Daily receipts
                </p>

                <input
                  data-testid="plan-edit-daily-receipt-limit"
                  type="number"
                  min="1"
                  value={
                    editing.features?.DAILY_RECEIPT_LIMIT?.limit ?? ""
                  }
                  onChange={(e) =>
                    setLimit(
                      "DAILY_RECEIPT_LIMIT",
                      e.target.value
                    )
                  }
                  placeholder="Unlimited"
                  className="w-full h-11 px-3 rounded-lg bg-secondary border border-border text-sm font-mono focus:border-primary focus:outline-none"
                />

                <p className="text-[10px] text-muted-foreground mt-1">
                  Kosong = unlimited
                </p>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setEditing(null)}
                data-testid="plan-edit-cancel"
                className="w-1/3 h-11 rounded-xl border border-border bg-secondary font-bold text-sm"
              >
                Cancel
              </button>

              <button
                onClick={save}
                data-testid="plan-edit-submit"
                className="w-2/3 h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm"
              >
                Save Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
